import { useEffect, useState, useCallback } from "react";
import { X, Trash2, UserPlus, KeyRound, ShieldCheck, HardHat } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../contexts/AuthContext";

const inputCls = "w-full bg-white border-2 border-[#23282B]/15 focus:border-[#2F5F80] focus:outline-none px-3 py-2.5 text-sm text-[#23282B]";

export function EmployeeAccessTab() {
  const { user: me } = useAuth();
  const [rows, setRows] = useState([]);
  const [modal, setModal] = useState(false);
  const [msg, setMsg] = useState(null);
  const [pw, setPw] = useState({}); // id -> new password value

  const refresh = useCallback(() => {
    api.get("/users").then((r) => setRows(r.data || [])).catch(() => {});
  }, []);
  useEffect(() => { refresh(); }, [refresh]);

  const patch = async (id, updates, okText) => {
    setMsg(null);
    try {
      await api.patch(`/users/${id}`, updates);
      setMsg({ kind: "ok", text: okText });
      refresh();
    } catch (e) { setMsg({ kind: "error", text: formatApiError(e) }); }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete the login for ${u.name || u.email}? They will no longer be able to sign in.`)) return;
    setMsg(null);
    try { await api.delete(`/users/${u.id}`); setMsg({ kind: "ok", text: "Login removed." }); refresh(); }
    catch (e) { setMsg({ kind: "error", text: formatApiError(e) }); }
  };

  return (
    <section className="bg-white border-2 border-[#2C3539]/10 p-5 md:p-8" data-testid="employee-access-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <div className="font-display text-2xl md:text-3xl uppercase text-[#23282B]">Employee Access</div>
          <p className="mt-1 text-sm text-[#23282B]/60">Website logins, contact info and access level. Employee access can upload photos and see only their own schedule.</p>
        </div>
        <button onClick={() => setModal(true)} data-testid="access-add-employee" className="inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-xs font-bold tracking-[0.15em] px-5 py-3 uppercase transition-colors">
          <UserPlus size={15} /> Add Employee
        </button>
      </div>

      {msg && (
        <div data-testid={`access-msg-${msg.kind}`} className={`mt-4 px-4 py-3 text-sm border-l-4 ${msg.kind === "ok" ? "bg-green-50 border-green-600 text-green-800" : "bg-red-50 border-red-500 text-red-700"}`}>
          {msg.text}
        </div>
      )}

      <div className="mt-6 space-y-3" data-testid="access-user-list">
        {rows.map((u) => (
          <UserRow
            key={u.id}
            u={u}
            isMe={Boolean(me && u.email === me.email)}
            pwValue={pw[u.id] || ""}
            onPwChange={(v) => setPw({ ...pw, [u.id]: v })}
            onSetPassword={() => {
              if ((pw[u.id] || "").length >= 6) { patch(u.id, { password: pw[u.id] }, "Password updated."); setPw({ ...pw, [u.id]: "" }); }
              else { setMsg({ kind: "error", text: "Password must be at least 6 characters." }); }
            }}
            onPatch={patch}
            onDelete={() => remove(u)}
          />
        ))}
      </div>

      {modal && <AddEmployeeModal onClose={() => setModal(false)} onSaved={() => { refresh(); setMsg({ kind: "ok", text: "Employee login created." }); }} />}
    </section>
  );
}

function UserRow({ u, isMe, pwValue, onPwChange, onSetPassword, onPatch, onDelete }) {
  return (
    <article className={`border-2 p-4 md:p-5 ${u.active ? "border-[#23282B]/10" : "border-dashed border-[#23282B]/15 bg-[#F1F3F5]"}`} data-testid={`access-user-${u.id}`}>
      <div className="flex flex-wrap items-start gap-4">
        <span className={`w-10 h-10 shrink-0 inline-flex items-center justify-center ${u.role === "admin" ? "bg-[#2F5F80] text-white" : "bg-[#23282B] text-[#9CC4DB]"}`}>
          {u.role === "admin" ? <ShieldCheck size={18} /> : <HardHat size={18} />}
        </span>
        <div className="flex-1 min-w-[200px]">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-display text-xl uppercase text-[#23282B]">{u.name || u.email}</span>
            {isMe && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#9CC4DB]/30 text-[#2F5F80] px-2 py-0.5">You</span>}
            {!u.active && <span className="text-[10px] font-bold uppercase tracking-wider bg-[#23282B]/10 text-[#23282B]/60 px-2 py-0.5">Not Active</span>}
          </div>
          <div className="mt-1 text-xs text-[#23282B]/70 flex flex-wrap gap-x-4 gap-y-0.5">
            {u.job_title && <span className="text-[#2F5F80] font-semibold">{u.job_title}</span>}
            <span>{u.email}</span>
            {u.phone && <span>{u.phone}</span>}
            {u.address && <span>{u.address}</span>}
          </div>
          <div className="mt-1.5 text-xs text-[#23282B]/50 italic" data-testid={`access-pw-row-${u.id}`}>
            Passwords are stored securely and can't be viewed. Use the field on the right to set a new one.
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2 w-full lg:w-auto">
          {/* Access level dropdown */}
          <select
            data-testid={`access-role-${u.id}`}
            value={u.role}
            onChange={(e) => onPatch(u.id, { role: e.target.value }, "Access level updated.")}
            className="text-xs font-semibold border-2 border-[#23282B]/20 focus:border-[#2F5F80] focus:outline-none px-2.5 py-2 bg-white text-[#23282B]"
          >
            <option value="admin">Full Access</option>
            <option value="employee">Employee Access</option>
          </select>

          {/* Active toggle */}
          <button
            onClick={() => onPatch(u.id, { active: !u.active }, u.active ? "Account deactivated." : "Account reactivated.")}
            data-testid={`access-active-${u.id}`}
            className={`text-xs font-bold uppercase tracking-[0.1em] px-3 py-2.5 transition-colors ${u.active ? "bg-green-600 hover:bg-green-700 text-white" : "bg-[#23282B]/70 hover:bg-[#23282B] text-white"}`}
          >
            {u.active ? "Active" : "Not Active"}
          </button>

          {/* Password reset */}
          <span className="inline-flex">
            <input
              data-testid={`access-password-${u.id}`}
              type="text"
              placeholder="New password"
              value={pwValue}
              onChange={(e) => onPwChange(e.target.value)}
              className="w-32 text-xs border-2 border-r-0 border-[#23282B]/20 focus:border-[#2F5F80] focus:outline-none px-2.5 py-2"
            />
            <button
              onClick={onSetPassword}
              data-testid={`access-set-password-${u.id}`}
              title="Set new password"
              className="border-2 border-[#23282B]/20 hover:border-[#2F5F80] hover:text-[#2F5F80] px-2.5 transition-colors"
            >
              <KeyRound size={14} />
            </button>
          </span>

          <button onClick={onDelete} data-testid={`access-delete-${u.id}`} className="p-2 text-red-600 hover:text-red-800" title="Delete login">
            <Trash2 size={16} />
          </button>
        </div>
      </div>
    </article>
  );
}

function AddEmployeeModal({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", phone: "", address: "", job_title: "", role: "employee" });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      await api.post("/users", form);
      onSaved();
      onClose();
    } catch (err) { setError(formatApiError(err)); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center px-5 py-8 overflow-y-auto" data-testid="access-add-modal">
      <form onSubmit={submit} className="bg-white w-full max-w-lg p-7 border-t-4 border-[#2F5F80] my-auto max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between">
          <div className="font-display text-2xl uppercase text-[#23282B]">Add Employee</div>
          <button type="button" onClick={onClose} data-testid="access-modal-close" className="p-1 text-[#23282B]/60 hover:text-[#23282B]"><X size={20} /></button>
        </div>
        <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4">
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Name *</span>
            <input data-testid="access-form-name" required value={form.name} onChange={set("name")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Job Title</span>
            <input data-testid="access-form-title" value={form.job_title} onChange={set("job_title")} className={inputCls} placeholder="e.g. Operator" /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Email (login) *</span>
            <input data-testid="access-form-email" required type="email" value={form.email} onChange={set("email")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Password * (min 6)</span>
            <input data-testid="access-form-password" required minLength={6} value={form.password} onChange={set("password")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Phone</span>
            <input data-testid="access-form-phone" value={form.phone} onChange={set("phone")} className={inputCls} /></label>
          <label className="block"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Access Level</span>
            <select data-testid="access-form-role" value={form.role} onChange={set("role")} className={inputCls}>
              <option value="employee">Employee Access</option>
              <option value="admin">Full Access</option>
            </select></label>
          <label className="block sm:col-span-2"><span className="block text-xs tracking-[0.18em] font-bold uppercase text-[#23282B]/70 mb-1.5">Address</span>
            <input data-testid="access-form-address" value={form.address} onChange={set("address")} className={inputCls} /></label>
        </div>
        {error && <div data-testid="access-form-error" className="mt-4 bg-red-50 border-l-4 border-red-500 px-3 py-2 text-sm text-red-700">{error}</div>}
        <button type="submit" disabled={busy} data-testid="access-form-submit" className="mt-6 inline-flex items-center gap-2 bg-[#2F5F80] hover:bg-[#234B66] text-white text-sm font-bold tracking-[0.15em] px-6 py-3 uppercase transition-colors disabled:opacity-50">
          {busy ? "Creating…" : "Create Login"}
        </button>
      </form>
    </div>
  );
}
